import React, { useEffect, useState } from "react";
import { Ban, CalendarDays, ChevronLeft, ChevronRight, LogIn, ShieldAlert, ShieldCheck, Unlock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useLang } from "@/i18n";
import { Panel, Spinner } from "@/pages/shared";

const REASON_STYLE = {
  honeypot: "bg-red-100 text-red-800", too_fast: "bg-red-100 text-red-800", challenge: "bg-orange-100 text-orange-800", turnstile: "bg-orange-100 text-orange-800",
  login_ok: "bg-emerald-100 text-emerald-800", logout: "bg-sky-100 text-sky-800", logout_auto: "bg-sky-100 text-sky-800", pause: "bg-violet-100 text-violet-800", ip_locked: "bg-rose-100 text-rose-800", ip_blocklist: "bg-slate-900 text-white", account_locked: "bg-amber-100 text-amber-800", wrong_password: "bg-slate-100 text-slate-700",
};
const isoDay = (d) => d.toISOString().slice(0, 10);
const shiftDay = (day, n) => { const d = new Date(day + "T12:00:00Z"); d.setUTCDate(d.getUTCDate() + n); return isoDay(d); };
const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "—");
const fmtDay = (day, lang) => new Date(day + "T12:00:00Z").toLocaleDateString(lang === "en" ? "en-GB" : lang === "es" ? "es-ES" : "nl-BE", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" }) : "—");

function Reason({ r, t }) {
  return <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${REASON_STYLE[r] || "bg-muted"}`}>{t(`ll_${r}`)}</span>;
}

function DayView({ t, lang }) {
  const [day, setDay] = useState(isoDay(new Date()));
  const [d, setD] = useState(null);
  useEffect(() => {
    setD(null);
    api.get("/auth/login-log/day", { params: { date: day } }).then(({ data }) => setD(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  }, [day]);
  const today = isoDay(new Date());
  return (
    <Panel title={t("ll_day_title")} testid="ll-day-panel" action={
      <div className="flex items-center gap-1">
        <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setDay(shiftDay(day, -1))} data-testid="ll-day-prev"><ChevronLeft className="h-4 w-4" /></Button>
        <Input type="date" value={day} max={today} onChange={(e) => e.target.value && setDay(e.target.value)} className="h-9 w-40 font-mono" data-testid="ll-day-date" />
        <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => setDay(shiftDay(day, 1))} disabled={day >= today} data-testid="ll-day-next"><ChevronRight className="h-4 w-4" /></Button>
        <Button variant="ghost" size="sm" className="h-9" onClick={() => setDay(today)} disabled={day === today} data-testid="ll-day-today">{t("view_today")}</Button>
      </div>
    }>
      <div className="px-5 pt-4 flex flex-wrap items-center gap-3">
        <p className="font-display text-lg font-bold capitalize flex items-center gap-2" data-testid="ll-day-heading"><CalendarDays className="h-5 w-5 text-primary" />{fmtDay(day, lang)}</p>
        {d && (
          <div className="ml-auto flex gap-2 text-xs font-semibold">
            <span className="rounded-full bg-emerald-100 text-emerald-800 px-2.5 py-1" data-testid="ll-day-ok"><LogIn className="inline h-3 w-3 mr-1" />{d.ok} {t("ll_ok")}</span>
            <span className="rounded-full bg-sky-100 text-sky-800 px-2.5 py-1" data-testid="ll-day-out">{d.out} {t("ll_out")}</span>
            <span className="rounded-full bg-slate-100 text-slate-700 px-2.5 py-1" data-testid="ll-day-failed">{d.failed} {t("ll_failed")}</span>
            <span className="rounded-full bg-red-100 text-red-800 px-2.5 py-1" data-testid="ll-day-bots">{d.bots} {t("ll_bots")}</span>
          </div>
        )}
      </div>
      <div className="max-h-[50vh] overflow-auto cb-scroll mt-2">
        {!d ? <Spinner small label={t("loading_data")} /> : d.events.length === 0 ? <p className="px-5 py-8 text-sm text-muted-foreground text-center" data-testid="ll-day-empty">{t("ll_day_none")}</p> : (
          <table className="w-full text-sm" data-testid="ll-day-table">
            <thead className="sticky top-0 bg-card text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border"><tr><th className="px-4 py-2 text-left font-medium">{t("ll_when")}</th><th className="px-4 py-2 text-left font-medium">{t("ll_identifier")}</th><th className="px-4 py-2 text-left font-medium">{t("ll_reason")}</th><th className="px-4 py-2 text-left font-medium">IP</th><th className="px-4 py-2 text-left font-medium">Browser</th></tr></thead>
            <tbody className="divide-y divide-border/70">
              {d.events.map((r, i) => (
                <tr key={i} className={["login_ok", "logout", "logout_auto", "pause"].includes(r.reason) ? "" : "bg-red-50/40 dark:bg-red-950/10"}><td className="px-4 py-2 font-mono text-xs">{fmtTime(r.at)}</td><td className="px-4 py-2 text-xs font-medium">{r.identifier}</td><td className="px-4 py-2"><Reason r={r.reason} t={t} /></td><td className="px-4 py-2 font-mono text-xs">{r.ip}</td><td className="px-4 py-2 text-[11px] text-muted-foreground truncate max-w-xs" title={r.ua}>{r.ua}</td></tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </Panel>
  );
}

export default function AdminLoginLog() {
  const { t, lang } = useLang();
  const [days, setDays] = useState(14);
  const [d, setD] = useState(null);
  const load = () => api.get("/auth/login-log", { params: { days } }).then(({ data }) => setD(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = async (ip, blocked) => {
    try {
      if (blocked) await api.delete(`/auth/ip-block/${encodeURIComponent(ip)}`);
      else await api.post("/auth/ip-block", { ip });
      toast.success(blocked ? `${t("ll_unblocked")} ${ip}` : `${t("ll_blocked")} ${ip}`);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const BlockBtn = ({ ip, blocked }) => (
    <Button size="sm" variant={blocked ? "outline" : "destructive"} className="h-7 px-2 text-xs" onClick={() => toggle(ip, blocked)} data-testid={`ll-toggle-${ip}`}>
      {blocked ? <><Unlock className="h-3.5 w-3.5 mr-1" />{t("ll_unblock")}</> : <><Ban className="h-3.5 w-3.5 mr-1" />{t("ll_block")}</>}
    </Button>
  );

  if (!d) return <Spinner label={t("loading_data")} />;
  const max = Math.max(1, ...d.days.map((x) => x.total));
  return (
    <div className="space-y-5" data-testid="login-log">
      <DayView t={t} lang={lang} />
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="ll-total"><p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t("ll_total")}</p><p className="mt-1 font-mono text-2xl font-semibold">{d.totals.total}</p></div>
        <div className="rounded-2xl border border-border bg-card p-4" data-testid="ll-bots"><p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t("ll_bots")}</p><p className="mt-1 font-mono text-2xl font-semibold text-red-700 flex items-center gap-2"><ShieldAlert className="h-5 w-5" />{d.totals.bots}</p></div>
        <div className="rounded-2xl border border-border bg-card p-4 flex items-center justify-between">
          <div><p className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{t("ll_period")}</p><p className="mt-1 text-sm text-muted-foreground">{t("ll_since")} {d.since}</p></div>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-9 rounded-md border border-input bg-background px-2 text-sm font-mono" data-testid="ll-days">
            {[7, 14, 30, 90].map((n) => <option key={n} value={n}>{n} d</option>)}
          </select>
        </div>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Panel title={t("ll_per_day")} testid="ll-days-panel">
          <div className="p-5 space-y-2">
            {d.days.length === 0 && <p className="text-sm text-muted-foreground flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" />{t("ll_none")}</p>}
            {d.days.map((x) => (
              <div key={x.day} className="grid grid-cols-[6rem_1fr_5rem] items-center gap-3 text-sm" data-testid={`ll-day-${x.day}`}>
                <span className="font-mono text-xs text-muted-foreground">{x.day}</span>
                <div className="h-5 rounded-full bg-muted overflow-hidden flex">
                  <div className="h-full bg-red-500" style={{ width: `${(x.bots / max) * 100}%` }} />
                  <div className="h-full bg-slate-400" style={{ width: `${((x.total - x.bots) / max) * 100}%` }} />
                </div>
                <span className="font-mono text-xs text-right"><span className="text-red-700 font-semibold">{x.bots}</span> / {x.total}</span>
              </div>
            ))}
          </div>
        </Panel>
        <Panel title={t("ll_per_ip")} testid="ll-ips-panel">
          <div className="max-h-96 overflow-auto cb-scroll">
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border"><tr><th className="px-4 py-2 text-left font-medium">IP</th><th className="px-4 py-2 text-right font-medium">{t("ll_bots")}</th><th className="px-4 py-2 text-right font-medium">{t("ll_total")}</th><th className="px-4 py-2 text-left font-medium">{t("ll_last")}</th><th className="px-4 py-2" /></tr></thead>
              <tbody className="divide-y divide-border/70">
                {d.ips.map((i) => (
                  <tr key={i.ip} className={i.blocked ? "bg-slate-100 dark:bg-slate-800/60" : ""} data-testid={`ll-ip-${i.ip}`}><td className="px-4 py-2 font-mono text-xs">{i.ip}{i.blocked && <span className="ml-2 rounded-full bg-slate-900 text-white px-1.5 text-[10px] font-semibold">{t("ll_blocked_badge")}</span>}</td><td className="px-4 py-2 text-right font-mono text-red-700 font-semibold">{i.bots}</td><td className="px-4 py-2 text-right font-mono">{i.count}</td><td className="px-4 py-2 text-xs text-muted-foreground">{fmt(i.last)}</td><td className="px-4 py-2 text-right"><BlockBtn ip={i.ip} blocked={i.blocked} /></td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>

      {d.blocklist.length > 0 && (
        <Panel title={t("ll_blocklist")} testid="ll-blocklist-panel">
          <ul className="divide-y divide-border/70">
            {d.blocklist.map((b) => (
              <li key={b.ip} className="px-5 py-2.5 flex items-center justify-between gap-3 text-sm" data-testid={`ll-blocked-${b.ip}`}>
                <span className="font-mono text-xs">{b.ip}</span>
                <span className="text-xs text-muted-foreground flex-1">{fmt(b.at)} · {b.by}{b.note ? ` · ${b.note}` : ""}</span>
                <BlockBtn ip={b.ip} blocked />
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <Panel title={t("ll_recent")} testid="ll-recent-panel">
        <div className="max-h-[50vh] overflow-auto cb-scroll">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border"><tr><th className="px-4 py-2 text-left font-medium">{t("ll_when")}</th><th className="px-4 py-2 text-left font-medium">IP</th><th className="px-4 py-2 text-left font-medium">{t("ll_reason")}</th><th className="px-4 py-2 text-left font-medium">{t("ll_identifier")}</th><th className="px-4 py-2 text-left font-medium">Browser</th></tr></thead>
            <tbody className="divide-y divide-border/70">
              {d.recent.map((r, i) => (
                <tr key={i}><td className="px-4 py-2 text-xs whitespace-nowrap">{fmt(r.at)}</td><td className="px-4 py-2 font-mono text-xs">{r.ip}</td><td className="px-4 py-2"><Reason r={r.reason} t={t} /></td><td className="px-4 py-2 text-xs">{r.identifier}</td><td className="px-4 py-2 text-[11px] text-muted-foreground truncate max-w-xs" title={r.ua}>{r.ua}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
